
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { cleanupAuthState } from "@/utils/authUtils";
import Logger from '@/utils/logger';
import { toastService } from '@/utils/toastService';

// Type definition for the consolidated user auth details
interface UserAuthDetails {
  status: string;
  is_admin: boolean;
  ban_reason: string | null;
  banned_at: string | null;
  banned_by: string | null;
  balance: number;
  discount_percentage: number;
  profile_exists: boolean;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any | null }>;
  signIn: (email: string, password: string) => Promise<{ error: any | null }>;
  signOut: () => Promise<void>;
  loading: boolean;
  userBalance: number;
  updateUserBalance: (amount: number) => Promise<void>;
  isAdmin: boolean;
  userDiscount: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userBalance, setUserBalance] = useState(0);
  const [userDiscount, setUserDiscount] = useState(0);

  // Direct auth operations to avoid circular dependencies
  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      cleanupAuthState();
      
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/auth/confirmed`,
        },
      });

      if (error) {
        Logger.error("Registration failed:", error.message);
        return { error };
      }

      Logger.info("Account created successfully for:", email);
      return { data, error: null };
    } catch (error: any) {
      Logger.error("Registration failed:", error.message || "An unexpected error occurred");
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      cleanupAuthState();
      
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        Logger.error("Login failed:", error.message);
        
        // Handle specific case of unconfirmed email
        if (error.message && error.message.includes("Email not confirmed")) {
          toastService.error("Your email has not been confirmed. Please check your inbox for the confirmation link.");
        }
        
        return { error };
      }

      Logger.info("User logged in:", email);
      return { error: null };
    } catch (error: any) {
      Logger.error("Login failed:", error.message || "An unexpected error occurred");
      return { error };
    }
  };

  const signOut = async () => {
    try {
      cleanupAuthState();
      
      // Clear session cache on logout
      try {
        sessionStorage.removeItem('session_ip_address');
        sessionStorage.removeItem('ip_detection_session');
      } catch (error) {
        // Silent failure
      }
      
      await supabase.auth.signOut({ scope: 'global' });
      
      setIsAdmin(false);
      setUserBalance(0);
      setUserDiscount(0);
      
      Logger.info("User logged out successfully");
      window.location.href = '/login';
    } catch (error: any) {
      Logger.error("Logout failed:", error.message || "Failed to log out");
    }
  };

  // Helper function to create a new profile
  const createNewProfile = async (userToCreate: User) => {
    const { error: insertError } = await supabase
      .from('profiles')
      .insert([
        { 
          id: userToCreate.id, 
          balance: 0.00, 
          is_admin: false,
          discount_percentage: 0,
          full_name: userToCreate.user_metadata?.full_name || null,
          avatar_url: userToCreate.user_metadata?.avatar_url || null
        }
      ]);
    
    if (insertError) {
      Logger.error("Error creating profile:", insertError);
    }
    
    // Set default values
    setUserBalance(0);
    setIsAdmin(false);
    setUserDiscount(0);
  };

  // Load user profile data using consolidated RPC function
  const loadUserProfile = async (userToLoad: User) => {
    try {
      
      // Use the consolidated RPC function to get all user details at once
      const { data: userDetailsData, error } = await supabase.rpc('get_user_auth_details', {
        p_user_id: userToLoad.id
      });

      if (error) {
        Logger.error("Error loading user profile:", error);
        // If RPC call fails, create a default profile
        await createNewProfile(userToLoad);
        return;
      }

      // Type cast the response to our interface
      const userDetails = userDetailsData as unknown as UserAuthDetails;
      
      if (!userDetails.profile_exists) {
        await createNewProfile(userToLoad);
      } else {
        setUserBalance(Number(userDetails.balance) || 0);
        setIsAdmin(userDetails.is_admin || false);
        setUserDiscount(Number(userDetails.discount_percentage) || 0);
      }
    } catch (error) {
      Logger.error("Unexpected error loading profile:", error);
      // Set default values on error
      setUserBalance(0);
      setIsAdmin(false);
      setUserDiscount(0);
    }
  };

  // Update user balance
  const updateUserBalance = async (amount: number) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ balance: amount })
        .eq('id', user.id);

      if (error) {
        Logger.error("Error updating balance:", error);
      } else {
        setUserBalance(amount);
      }
    } catch (error) {
      Logger.error("Unexpected error updating balance:", error);
    }
  };

  // Auth state listener with proper session management
  useEffect(() => {
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        // Only load profile data if user is confirmed (for email confirmation flow)
        if (event === 'SIGNED_IN' && currentSession?.user && currentSession.user.email_confirmed_at) {
          // Use setTimeout to defer profile loading and prevent race conditions
          setTimeout(() => {
            loadUserProfile(currentSession.user);
          }, 100);
        } else if (event === 'SIGNED_OUT') {
          setIsAdmin(false);
          setUserBalance(0);
          setUserDiscount(0);
        }
        
        setLoading(false);
      }
    );

    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          Logger.error("Error getting initial session:", error);
        }
        
        // Only load profile if user is confirmed (for email confirmation flow)
        if (initialSession && initialSession.user.email_confirmed_at) {
          setSession(initialSession);
          setUser(initialSession.user);
          // Use setTimeout to defer profile loading and prevent race conditions
          setTimeout(() => {
            loadUserProfile(initialSession.user);
          }, 100);
        } else if (initialSession) {
          // Set session but don't load profile for unconfirmed users
          setSession(initialSession);
          setUser(initialSession.user);
        }
      } catch (error) {
        Logger.error("Unexpected error getting session:", error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Stabilize function references with useCallback
  const signUpCallback = useCallback(signUp, []);
  const signInCallback = useCallback(signIn, []);
  const signOutCallback = useCallback(signOut, []);
  const updateUserBalanceCallback = useCallback(updateUserBalance, [user]);

  // Memoize the context value
  const value = useMemo(() => ({
    session,
    user,
    signUp: signUpCallback,
    signIn: signInCallback,
    signOut: signOutCallback,
    loading,
    userBalance,
    updateUserBalance: updateUserBalanceCallback,
    isAdmin,
    userDiscount
  }), [session, user, loading, userBalance, isAdmin, userDiscount, signUpCallback, signInCallback, signOutCallback, updateUserBalanceCallback]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
